import { useEffect, useState } from "react";
import api from "../lib/api";
import { Video, Image as ImgIcon, TrendingUp, TrendingDown, Minus, AlertCircle } from "lucide-react";

const TONE = {
  green: "bg-emerald-50 border-emerald-300 text-emerald-800",
  amber: "bg-amber-50 border-amber-300 text-amber-800",
  red:   "bg-red-50 border-red-300 text-red-800",
};

function TrendPill({ delta }) {
  if (typeof delta !== "number") return null;
  const Icon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-medium ml-1">
      <Icon size={10}/>
      {delta > 0 ? "+" : ""}{delta} vs last
    </span>
  );
}

/**
 * SMCM Weekly Content Quota — 3 videos + 4 posts per ISO week.
 * Shown to SMCM (own office), BDOM + Founder (all offices).
 */
export default function SocialQuotaCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get("/social-media/weekly-quota")
      .then(r => setData(r.data))
      .catch(() => setErr(true));
  }, []);
  if (err || !data) return null;
  const tone = data.tone || "amber";
  const needVideos = Math.max(0, data.min_videos - data.videos);
  const needPosts = Math.max(0, data.min_posts - data.posts);
  return (
    <div className={`aii-card p-4 border-l-4 ${tone === "green" ? "border-emerald-500" : tone === "amber" ? "border-amber-500" : "border-red-500"}`} data-testid="social-quota-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Social media quota · this week</div>
          <h3 className="text-lg font-semibold flex items-center gap-2 mt-0.5">
            {tone !== "green" && <AlertCircle className={tone === "red" ? "text-red-600" : "text-amber-600"} size={18}/>}
            {tone === "green"
              ? "This week's quota is locked in ✓"
              : `Content Department inactivity · ship ${needVideos + needPosts} more this week`}
          </h3>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className={`border rounded p-3 ${TONE[data.tone_videos] || TONE.amber}`} data-testid="quota-videos">
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest opacity-80">
            <Video size={12}/> Videos
          </div>
          <div className="text-2xl font-display font-semibold leading-tight mt-0.5">
            {data.videos}<span className="text-base opacity-70"> / {data.min_videos}</span>
            <TrendPill delta={data.trend_videos}/>
          </div>
          {data.tone_videos !== "green" && (
            <div className="text-[11px] mt-0.5">{data.min_videos - data.videos} more video{data.min_videos - data.videos === 1 ? "" : "s"} needed by Sunday</div>
          )}
        </div>
        <div className={`border rounded p-3 ${TONE[data.tone_posts] || TONE.amber}`} data-testid="quota-posts">
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest opacity-80">
            <ImgIcon size={12}/> Posts
          </div>
          <div className="text-2xl font-display font-semibold leading-tight mt-0.5">
            {data.posts}<span className="text-base opacity-70"> / {data.min_posts}</span>
            <TrendPill delta={data.trend_posts}/>
          </div>
          {data.tone_posts !== "green" && (
            <div className="text-[11px] mt-0.5">{data.min_posts - data.posts} more post{data.min_posts - data.posts === 1 ? "" : "s"} needed by Sunday</div>
          )}
        </div>
      </div>
      <div className="text-[10px] text-[color:var(--aii-muted)] mt-2">
        Week of {data.week_start} → {data.week_end} · Founder + BDOM are looped in if the quota isn't met by Wednesday.
      </div>
    </div>
  );
}
